import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Type, Undo2, X, Loader2, Check } from "lucide-react";
import { api, apiErr } from "@/lib/api";

export default function PhotoAnnotator({ mediaId, onClose, onSaved }) {
  const canvasRef = useRef(null);
  const baseImg = useRef(null);
  const drag = useRef(null);
  const [tool, setTool] = useState("arrow");
  const [annos, setAnnos] = useState([]);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let url;
    api.get(`/media/${mediaId}/file`, { responseType: "blob" }).then((r) => {
      url = URL.createObjectURL(r.data);
      const img = new Image();
      img.onload = () => {
        const maxW = 1200, s = Math.min(1, maxW / img.width);
        const w = Math.round(img.width * s), h = Math.round(img.height * s);
        baseImg.current = img;
        const c = canvasRef.current;
        if (c) { c.width = w; c.height = h; }
        setReady(true); setAnnos([]);
      };
      img.src = url;
    }).catch(() => toast.error("Could not load photo"));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [mediaId]);

  useEffect(() => {
    if (!ready || !canvasRef.current || !baseImg.current) return;
    const c = canvasRef.current, ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.drawImage(baseImg.current, 0, 0, c.width, c.height);
    ctx.lineWidth = Math.max(3, c.width / 200); ctx.strokeStyle = "#F59E0B"; ctx.fillStyle = "#F59E0B";
    ctx.font = `${Math.max(16, c.width / 45)}px sans-serif`;
    for (const a of annos) {
      if (a.type === "arrow") {
        ctx.beginPath(); ctx.moveTo(a.x1, a.y1); ctx.lineTo(a.x2, a.y2); ctx.stroke();
        const ang = Math.atan2(a.y2 - a.y1, a.x2 - a.x1), hh = c.width / 40;
        ctx.beginPath(); ctx.moveTo(a.x2, a.y2);
        ctx.lineTo(a.x2 - hh * Math.cos(ang - 0.4), a.y2 - hh * Math.sin(ang - 0.4));
        ctx.lineTo(a.x2 - hh * Math.cos(ang + 0.4), a.y2 - hh * Math.sin(ang + 0.4));
        ctx.closePath(); ctx.fill();
      } else {
        ctx.fillStyle = "#000"; ctx.fillRect(a.x - 2, a.y - 18, ctx.measureText(a.text).width + 8, 24);
        ctx.fillStyle = "#F59E0B"; ctx.fillText(a.text, a.x + 2, a.y);
      }
    }
  }, [annos, ready]);

  const evtXY = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (canvasRef.current.width / r.width), y: (e.clientY - r.top) * (canvasRef.current.height / r.height) };
  };
  const onDown = (e) => {
    const p = evtXY(e);
    if (tool === "label") { const t = window.prompt("Label text (e.g. crack here)"); if (t) setAnnos((a) => [...a, { type: "label", x: p.x, y: p.y, text: t }]); return; }
    drag.current = p;
  };
  const onUp = (e) => { if (tool === "arrow" && drag.current) { const p = evtXY(e); setAnnos((a) => [...a, { type: "arrow", x1: drag.current.x, y1: drag.current.y, x2: p.x, y2: p.y }]); } drag.current = null; };

  const save = async () => {
    if (!annos.length) { toast.error("Add at least one arrow or label first"); return; }
    setBusy(true);
    const blob = await new Promise((res) => canvasRef.current.toBlob(res, "image/jpeg", 0.85));
    try {
      const fd = new FormData(); fd.append("file", blob, "annotated.jpg");
      await api.post(`/media/${mediaId}/annotate`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Annotations saved to the photo"); onSaved?.(); onClose?.();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };

  const tb = (active) => `inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs ${active ? "bg-amber-500 text-black" : "border border-slate-700 text-slate-300"}`;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/75 p-4" data-testid="photo-annotator">
      <div className="w-full max-w-3xl rounded-2xl border border-slate-700 bg-[#0d1420] p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <button data-testid="annot-tool-arrow" onClick={() => setTool("arrow")} className={tb(tool === "arrow")}><ArrowUpRight className="h-3.5 w-3.5" /> Arrow</button>
            <button data-testid="annot-tool-label" onClick={() => setTool("label")} className={tb(tool === "label")}><Type className="h-3.5 w-3.5" /> Label</button>
            <button data-testid="annot-undo" onClick={() => setAnnos((a) => a.slice(0, -1))} className="inline-flex items-center gap-1 rounded-lg border border-slate-700 px-2.5 py-1 text-xs text-slate-300"><Undo2 className="h-3.5 w-3.5" /> Undo</button>
            <span className="font-mono-tac text-[10px] text-slate-500">{tool === "arrow" ? "drag to point" : "tap to drop a label"}</span>
          </div>
          <button data-testid="annot-close" onClick={onClose} className="rounded-full p-1 text-slate-400 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="relative">
          <canvas ref={canvasRef} onPointerDown={onDown} onPointerUp={onUp} style={{ touchAction: "none" }}
            className="w-full cursor-crosshair rounded-xl border border-slate-800 bg-black" />
          {!ready && <div className="absolute inset-0 grid place-items-center rounded-xl bg-black/60 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>}
        </div>
        <div className="mt-3 flex gap-2">
          <button data-testid="annot-save" onClick={save} disabled={busy || !ready} className="btn-amber inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {busy ? "Saving…" : "Save annotations"}
          </button>
          <button onClick={onClose} className="rounded-full border border-slate-700 px-4 py-2 text-sm text-slate-300">Cancel</button>
        </div>
      </div>
    </div>
  );
}
